// ============================================================
//  Vertragsgrundlage: Der RISE-Vertrag ist nicht „19.900 FUE", sondern eine angenommene Menge
//  je Lizenzklasse, die mit den Gewichten auf 19.900 FUE summiert. Diese Annahme ist eine
//  Eingabe (Einstellungen, Vertrag), keine Konstante — die Summe ist überall die Vertragszahl.
//  Die Geldlogik bleibt: Delta = Bedarf − Vertragssumme, Euro = Delta × Preis je FUE.
// ============================================================
import { useSyncExternalStore } from "react";
import { DEFAULT_RATIO, type RatioTable, weight } from "../domain/rules";
import type { LicenseClass } from "../domain/types";

export type ContractClass = Exclude<LicenseClass, "NotRelevant">;
export type ContractBasis = Record<ContractClass, number>;

/** Standardannahme (nah am heutigen Mix): 1.000 × 2 + 14.000 × 1 + 13.000 × 0,2 + 39.000 × 1/30 = 19.900 FUE. */
export const DEFAULT_CONTRACT_BASIS: ContractBasis = { Developer: 1_000, Advanced: 14_000, Core: 13_000, SelfService: 39_000 };
export const CONTRACT_CLASSES: ContractClass[] = ["Developer", "Advanced", "Core", "SelfService"];

const KEY = "rise-cockpit.contract.v1";
let basis: ContractBasis = load();
const listeners = new Set<() => void>();

function load(): ContractBasis {
  try {
    const raw = localStorage.getItem(KEY); if (!raw) return { ...DEFAULT_CONTRACT_BASIS };
    const j = JSON.parse(raw) as Partial<ContractBasis>;
    const out = { ...DEFAULT_CONTRACT_BASIS };
    for (const c of CONTRACT_CLASSES) { const v = Number(j[c]); if (Number.isFinite(v) && v >= 0) out[c] = Math.round(v); }
    return out;
  } catch { return { ...DEFAULT_CONTRACT_BASIS }; }
}
function persist() { try { localStorage.setItem(KEY, JSON.stringify(basis)); } catch { /* nur im Speicher */ } }
function set(next: ContractBasis) { basis = next; persist(); for (const l of listeners) l(); }
function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }
const snapshot = () => basis;

/** FUE-Summe einer Vertragsgrundlage mit der Verhältnistabelle. */
export function contractTotal(b: ContractBasis = basis, ratio: RatioTable = DEFAULT_RATIO): number {
  return CONTRACT_CLASSES.reduce((s, c) => s + b[c] * weight(c, ratio), 0);
}

export const contractStore = {
  get: snapshot,
  set(cls: ContractClass, count: number) { if (!Number.isFinite(count) || count < 0) return; set({ ...basis, [cls]: Math.round(count) }); },
  reset() { set({ ...DEFAULT_CONTRACT_BASIS }); },
  isDefault() { return CONTRACT_CLASSES.every((c) => basis[c] === DEFAULT_CONTRACT_BASIS[c]); },
};

/** Nicht-reaktiv, für Domain-Code: die Vertragszahl (FUE) aus der aktuellen Grundlage. */
export function contractedFue(ratio: RatioTable = DEFAULT_RATIO): number { return contractTotal(basis, ratio); }

/** Reaktiv: Grundlage je Klasse und Summe. */
export function useContract(ratio: RatioTable = DEFAULT_RATIO): { basis: ContractBasis; total: number; isDefault: boolean } {
  const b = useSyncExternalStore(subscribe, snapshot, snapshot);
  return { basis: b, total: contractTotal(b, ratio), isDefault: CONTRACT_CLASSES.every((c) => b[c] === DEFAULT_CONTRACT_BASIS[c]) };
}
